import { useContext, useEffect, ReactNode } from 'react'
import { EmptyFieldsWarningAppendix } from './Generic'
import Button from '../Button'
import { cn } from '../../utils'
import { useEmptyFieldsCheck } from '../../empty-checks'
import BefehlForm from './BefehlForm'
import { Befehl } from '../../models'
import { useTranslation } from 'react-i18next'
import { DoneIcon32 } from '../../icons'
import MarkAuftraegeAsDoneContext from '../../contexts/MarkAuftraegeAsDoneContext'

export interface BefehlCardProps {
  befehl: Befehl
  'data-testid': string
  onGelesen?: (id: string) => void
  isGelesen?: boolean
  showGelesenButton?: boolean
  displayHeaderOnly?: boolean
  befehlsnachrichtIsQuittiert?: boolean
  activateEmptyFieldWarnings?: boolean
  children?: ReactNode
}

/**
 * Renders a Befehl in a card.
 * Shows a warning in the card if the Befehl has empty fields.
 */
export default function BefehlCard({
  befehl,
  'data-testid': testId,
  onGelesen,
  isGelesen = false,
  showGelesenButton = false,
  displayHeaderOnly = false,
  befehlsnachrichtIsQuittiert = false,
  activateEmptyFieldWarnings = false,
  children
}: Readonly<BefehlCardProps>) {
  const { t } = useTranslation('ui-befehle')
  const hasEmptyFields = useEmptyFieldsCheck(befehl, activateEmptyFieldWarnings)
  const context = useContext(MarkAuftraegeAsDoneContext)

  useEffect(() => {
    context?.setIsBefehlsnachrichtQuittiert(befehlsnachrichtIsQuittiert)
  }, [context, befehl, befehlsnachrichtIsQuittiert])

  return (
    <div
      className={cn(
        'rounded',
        hasEmptyFields && activateEmptyFieldWarnings
          ? 'bg-red-100 border-red-200 border-2 dark:bg-red-900/30 dark:border-red-700'
          : 'border border-gray-300 dark:border-gray-700'
      )}
    >
      <div
        className={cn(
          'cmp-card',
          'flex flex-col text-left shadow-md rounded px-1 py-1 dark:text-gray-200 dark:bg-cool-gray-800',
          {
            'rounded-t-sm rounded-b-none':
              hasEmptyFields && activateEmptyFieldWarnings
          }
        )}
      >
        <BefehlForm
          befehl={befehl}
          readOnly={true}
          displayHeaderOnly={displayHeaderOnly}
          data-testid={testId}
          activateEmptyFieldWarnings={activateEmptyFieldWarnings}
          onSubmit={() => {}}
        />
        {children}
        {showGelesenButton && onGelesen && (
          <Button
            variant="brand-primary"
            className="w-full mt-1.5"
            onClick={() => onGelesen(befehl.frontendId)}
            disabled={isGelesen}
            data-testid={`${testId}-gelesen-button`}
          >
            <DoneIcon32
              className="shrink-0"
              data-testid={`${testId}-gelesen-button-icon`}
            />
            <span
              className="pl-0.5"
              data-testid={`${testId}-gelesen-button-text`}
            >
              {t('card.markAsRead')}
            </span>
          </Button>
        )}
      </div>

      {activateEmptyFieldWarnings && hasEmptyFields && (
        <EmptyFieldsWarningAppendix
          data-testid={`${testId}-empty-fields-warning`}
        />
      )}
    </div>
  )
}
